import { IconLoaderService } from '@app/core/services/icon-loader.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SubSink } from 'subsink';
import { FormlyModule } from '@ngx-formly/core';
import { MosaicGalleryComponent } from '@app/shared/components/mosaic-gallery/mosaic-gallery.component';
import { PdfViewerComponent } from '@app/shared/components/pdf-viewer/pdf-viewer.component';
import { HttpImgSrcPipe } from '@app/pipes/http-img-src.pipe';
import { MiscService } from '@app/core/services/misc.service';
import { LocaleNumberPipe } from '@app/pipes/locale-number.pipe';
import { Constants } from '@app/core/services/constants';
import { DynamicIconComponent } from "@app/shared/components/dynamic-icon";
import { MediaService } from '@app/core/services/media.service';
import { firstValueFrom } from 'rxjs';

export interface Medium {
  fileId: string;
  mediaType: string;
  originalname: string;
};

export interface MediaModel {
  media: Medium[];
  selectedFiles: File[];
}

@Component({
  selector: 'app-formly-field-picture',
  templateUrl: './formly-field-picture.component.html',
  styleUrls: ['./formly-field-picture.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonicModule,
    CommonModule,
    FormsModule,
    FormlyModule,
    TranslateModule,
    ReactiveFormsModule,
    MosaicGalleryComponent,
    PdfViewerComponent,
    HttpImgSrcPipe,
    DynamicIconComponent
],
  providers: [
    LocaleNumberPipe,
  ]
})
export class FormlyFieldPictureComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  
  public mediaModel: MediaModel = {
    media: [],
    selectedFiles: [],
  };
  public maxFiles = Constants.maxFiles;
  private subsink = new SubSink();
  private filePreviewUrls = new Map<File, string>();

  public readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  constructor(private miscService: MiscService,
              private mediaService: MediaService,
              private iconLoaderService: IconLoaderService,
              private changeDetectorRef: ChangeDetectorRef,
              private localeNumberPipe: LocaleNumberPipe) {
    super();
  }

  ngOnInit(): void {
    // Subscribe to formControl value changes to update localValue
    this.iconLoaderService.loadIcons(['fa-regular fa-camera', 'fa-solid fa-trash', 'fa-solid fa-eye-slash']);
    this.subsink.sink = this.formControl.valueChanges.subscribe(value => {
      this.updateLocalValue(value);
    });

    // Initialize with current value
    this.updateLocalValue(this.formControl.value);
  }

  private updateLocalValue(modelValue: any): void {
    const self = this;
    this.maxFiles = this.field?.props?.['maxFiles'] || Constants.maxFiles;
    const previousSelectedFiles = [...this.mediaModel.selectedFiles];

    if (modelValue && typeof modelValue === 'object') {
      this.mediaModel.media = modelValue.media || [];
      this.mediaModel.selectedFiles = modelValue.selectedFiles || [];
    } else {
      this.mediaModel = { media: [], selectedFiles: [] };
    }
    this.cleanupRemovedFilePreviews(previousSelectedFiles, this.mediaModel.selectedFiles);
    // self.changeDetectorRef.detectChanges();
    self.onChange(this.mediaModel);
    self.changeDetectorRef.markForCheck();
  }
  
  ngOnDestroy(): void {
    this.subsink.unsubscribe();
    this.revokeAllFilePreviews();
  }

  public onFileSelect(event: any) {
    const files = Array.from(event.target.files) as File[];
    const totalFiles = this.mediaModel.selectedFiles.length + this.mediaModel.media.length;

    // Validate file count
    if (totalFiles + files.length > this.maxFiles) {
      this.miscService.showErrorToast('ERRORS.FILE_NUMBER_EXCEEDED',true, { maxFiles: this.maxFiles });
      return;
    }

    // Validate file sizes and types
    for (const file of files) {
      if (file.size > this.MAX_FILE_SIZE) {
        this.miscService.showErrorToast('ERRORS.FILE_TOO_LARGE', true, { filename: file.name, maxFileSize: `${this.localeNumberPipe.transform(this.MAX_FILE_SIZE / 1024 / 1024, 'decimal')} MB` });
        continue;
      }

      if (!this.isValidFileType(file)) {
        this.miscService.showErrorToast('ERRORS.UNSUPPORTED_FORMAT', true, { filename: file.name });
        continue;
      }

      this.mediaModel.selectedFiles.push(file);
    }

    this.formControl.setValue(this.mediaModel);
    this.formControl.markAsDirty();
    this.changeDetectorRef.detectChanges;

    // Clear the input
    event.target.value = '';
  }

  public getImageMedia(): Medium[] {
    const media = this.mediaModel.media
    .filter(medium => this.isImage(medium.mediaType))
    return media;
  }

  public getNonImageMedia(): Medium[] {
    const media = this.mediaModel.media
    .filter(medium => !this.isImage(medium.mediaType))
    return media;
  }

  /**
   * Returns whether the file is an image.
   * The fileId should have been previously loaded via downloadMedia().
   * @param fileId 
   * @returns boolean
   */
  public isImage(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }

  /** 
   * Returns the media icon name for the given fileId. 
   * The fileId should have been previously loaded via downloadMedia().
   * @param fileId 
   * @returns icon name
   */
  public getMediaIcon(mimeType: string): string {
    if (mimeType.indexOf('image') !== -1) return 'image-outline';
    if (mimeType.indexOf('video') !== -1) return 'videocam-outline';
    if (mimeType.indexOf('pdf') !== -1) return 'document-outline';
    return 'attach-outline';
  }

  private isValidFileType(file: File): boolean {
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif',
      'image/heic', 'image/bmp', 'image/tiff',
      'video/mp4', 'video/webm', 'video/ogg', 'video/mov',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel',
      'text/plain'
    ];
    return allowedTypes.includes(file.type);
  }

  public removeFile(index: number) {
    const removed = this.mediaModel.selectedFiles[index];
    if (removed) {
      this.revokeFilePreview(removed);
    }
    this.mediaModel.selectedFiles.splice(index, 1);
    this.formControl.setValue(this.mediaModel);
    this.formControl.markAsDirty();
  }

  public getFileIcon(file: File): string {
    if (file?.type?.startsWith('image/')) return 'image-outline';
    if (file?.type?.startsWith('video/')) return 'videocam-outline';
    if (file?.type === 'application/pdf') return 'document-outline';
    return 'attach-outline';
  }

  public getTotalMediaCount(): number {
    return this.mediaModel.selectedFiles.length + this.mediaModel.media.length;
  }

  public isImageFile(file: File): boolean {
    return file?.type?.startsWith('image/');
  }

  public getFileExtension(filename: string): string {
    const ext = filename.split('.').pop()?.toUpperCase();
    return ext || '';
  }

  public getFilePreview(file: File): string {
    const existingPreview = this.filePreviewUrls.get(file);
    if (existingPreview) {
      return existingPreview;
    }

    const preview = URL.createObjectURL(file);
    this.filePreviewUrls.set(file, preview);
    return preview;
  }

  public removeExistingMedia(index: number) {
    this.mediaModel.media.splice(index, 1);
    this.formControl.setValue(this.mediaModel);
    this.formControl.markAsDirty();
  }

  public async downloadItem(medium: Medium): Promise<void> {
    try {
      await firstValueFrom(this.mediaService.downloadMedia(medium.fileId, medium.originalname));
    } catch {
      this.miscService.showErrorToast('ERRORS.GENERIC', true);
    }
  }

  private cleanupRemovedFilePreviews(previousFiles: File[], currentFiles: File[]): void {
    const currentSet = new Set(currentFiles);
    for (const file of previousFiles) {
      if (!currentSet.has(file)) {
        this.revokeFilePreview(file);
      }
    }
  }

  private revokeFilePreview(file: File): void {
    const existingPreview = this.filePreviewUrls.get(file);
    if (existingPreview) {
      URL.revokeObjectURL(existingPreview);
      this.filePreviewUrls.delete(file);
    }
  }

  private revokeAllFilePreviews(): void {
    for (const preview of this.filePreviewUrls.values()) {
      URL.revokeObjectURL(preview);
    }
    this.filePreviewUrls.clear();
  }
}
